import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Target,
  Heart,
  Eye,
  Zap,
  Linkedin,
  Twitter,
  Mail,
} from "lucide-react";
import PageShell from "@/components/site/PageShell";
import FinalCTA from "@/components/site/FinalCTA";

const FOUNDER_IMG =
  "https://customer-assets.emergentagent.com/job_echowings-premium/artifacts/zurefzue_image.png";

const STATS = [
  { value: "250+", label: "Websites Built" },
  { value: "150+", label: "Happy Clients" },
  { value: "$25M+", label: "Tracked Client Revenue" },
  { value: "10+", label: "Years Experience" },
];

const VALUES = [
  {
    icon: Target,
    title: "Outcomes Over Optics",
    desc: "Pretty design is table stakes. We measure success in revenue, qualified leads and rankings — not screenshots.",
  },
  {
    icon: Heart,
    title: "Partner, Not Vendor",
    desc: "We treat your business like our own. Dedicated point of contact, weekly demos, real conversations.",
  },
  {
    icon: Eye,
    title: "Radical Transparency",
    desc: "You see every metric, every experiment and every decision. No black boxes, no agency fog.",
  },
  {
    icon: Zap,
    title: "AI-First, Always",
    desc: "We build for where search is going — ChatGPT, Gemini, Perplexity — not where it's been.",
  },
];

const TIMELINE = [
  {
    year: "2014",
    title: "The first website",
    desc: "Matthew ships his first client site — a local contractor — and watches them triple inbound calls in 90 days.",
  },
  {
    year: "2018",
    title: "Specialty in service businesses",
    desc: "After dozens of projects, EchoWings settles on a clear niche: home-service and local businesses where revenue impact is measurable.",
  },
  {
    year: "2022",
    title: "SEO + branding becomes a system",
    desc: "We codify our growth process — combining custom websites, technical SEO, branding and content into one engine.",
  },
  {
    year: "2024",
    title: "GEO arrives",
    desc: "We become one of the first agencies to formalize Generative Engine Optimization as a service, ahead of the AI search wave.",
  },
  {
    year: "2026",
    title: "EchoWings today",
    desc: "150+ clients, $25M+ in tracked revenue, and a team of senior operators obsessed with compounding growth.",
  },
];

export default function AboutPage() {
  return (
    <PageShell testId="about-page">
      {/* Hero */}
      <section className="relative pt-16 pb-12 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] radial-glow opacity-40 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              About EchoWings Media
            </div>
            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02]">
              We build{" "}
              <span className="text-gradient-brand">growth engines</span> —
              <br />
              not just websites.
            </h1>
            <p className="mt-6 text-lg text-white/65 max-w-2xl mx-auto">
              EchoWings Media is a premium digital growth studio for ambitious
              service businesses. We craft websites, SEO + GEO systems and
              brands that turn local operators into category leaders.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative pb-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="rounded-3xl border border-white/10 bg-gradient-to-r from-blue-600/[0.08] via-cyan-400/[0.05] to-blue-600/[0.08] p-8 md:p-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {STATS.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  data-testid={`about-stat-${i}`}
                >
                  <div className="font-display text-3xl md:text-5xl font-extrabold text-gradient-brand">
                    {s.value}
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-widest text-white/50 font-semibold">
                    {s.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="section relative" data-testid="about-founder">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 relative"
          >
            <div className="absolute -inset-4 bg-gradient-to-tr from-blue-600/30 to-cyan-400/20 rounded-[2rem] blur-2xl" />
            <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,85,255,0.25)]">
              <img
                src={FOUNDER_IMG}
                alt="Matthew Burgess, Founder & CEO"
                className="w-full h-auto block aspect-square object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
              <div className="absolute left-5 right-5 bottom-5 glass-card rounded-2xl px-5 py-4 flex items-center justify-between">
                <div>
                  <div className="font-display text-lg font-semibold">
                    Matthew Burgess
                  </div>
                  <div className="text-xs text-cyan-300 uppercase tracking-widest font-semibold">
                    Founder &amp; CEO
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href="#"
                    data-testid="about-founder-linkedin"
                    aria-label="LinkedIn"
                    className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-cyan-400 hover:border-cyan-500/40 transition-colors"
                  >
                    <Linkedin className="w-3.5 h-3.5" />
                  </a>
                  <a
                    href="#"
                    data-testid="about-founder-twitter"
                    aria-label="Twitter"
                    className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-cyan-400 hover:border-cyan-500/40 transition-colors"
                  >
                    <Twitter className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 28 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              Founder Letter
            </div>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-bold tracking-tight leading-[1.05]">
              Built by an operator,{" "}
              <span className="text-gradient-brand">
                not an account manager.
              </span>
            </h2>
            <p className="mt-6 text-white/65 leading-relaxed">
              I started EchoWings Media after a decade growing service
              businesses from the inside. I&apos;ve worn every hat — from
              hand-coding sites and writing SEO articles, to closing deals and
              running paid ads.
            </p>
            <p className="mt-4 text-white/65 leading-relaxed">
              What I kept seeing was the same gap: agencies focused on aesthetics
              instead of outcomes, and contractors treated like ticket numbers.
              EchoWings exists to fix that — premium craft, senior partnership
              and measurable revenue impact as the only metric that matters.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/book-audit"
                data-testid="about-founder-cta"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-400 text-black font-semibold px-6 py-3.5 rounded-full hover:shadow-[0_0_30px_rgba(0,229,255,0.4)] transition-all"
              >
                Work With Matthew
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="mailto:matthew@echowingsmedia.com"
                data-testid="about-founder-email"
                className="inline-flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white/10 transition-all backdrop-blur-md"
              >
                <Mail className="w-4 h-4" />
                matthew@echowingsmedia.com
              </a>
            </div>
            <div className="mt-4">
              <Link
                to="/team"
                data-testid="about-meet-team"
                className="inline-flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300 font-semibold"
              >
                Meet the whole team <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="section pt-0" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              Our Values
            </div>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-bold tracking-tight">
              How we think. How we work.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                data-testid={`about-value-${i}`}
                className="rounded-3xl border border-white/10 bg-white/[0.025] p-6 hover:border-cyan-500/30 hover:bg-white/[0.04] transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-400 flex items-center justify-center shadow-[0_8px_20px_rgba(0,229,255,0.2)]">
                  <v.icon className="w-5 h-5 text-black" />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold">
                  {v.title}
                </h3>
                <p className="mt-2 text-sm text-white/55 leading-relaxed">
                  {v.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section pt-0" data-testid="about-timeline">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
              Our Journey
            </div>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-bold tracking-tight">
              A decade of compounding craft.
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-cyan-500/30 to-transparent" />
            <div className="space-y-8">
              {TIMELINE.map((t, i) => (
                <motion.div
                  key={t.year}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  data-testid={`about-timeline-${i}`}
                  className={`relative grid md:grid-cols-2 gap-6 items-center ${
                    i % 2 === 1 ? "md:[direction:rtl]" : ""
                  }`}
                >
                  <div
                    className={`pl-12 md:pl-0 ${
                      i % 2 === 1 ? "md:[direction:ltr] md:text-right" : ""
                    }`}
                  >
                    <div className="relative">
                      <div
                        className={`absolute -left-12 md:left-auto top-2 w-4 h-4 rounded-full bg-gradient-to-br from-blue-600 to-cyan-400 shadow-[0_0_20px_rgba(0,229,255,0.6)] ${
                          i % 2 === 1
                            ? "md:-right-[calc(50%+0.5rem)] md:translate-x-1/2"
                            : "md:-right-[calc(50%+0.5rem)] md:translate-x-1/2"
                        }`}
                      />
                      <div className="text-xs uppercase tracking-widest text-cyan-300 font-semibold">
                        {t.year}
                      </div>
                      <div className="mt-2 font-display text-2xl font-bold">
                        {t.title}
                      </div>
                      <p className="mt-2 text-white/55 leading-relaxed">
                        {t.desc}
                      </p>
                    </div>
                  </div>
                  <div />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </PageShell>
  );
}
